import { oc } from "@orpc/contract";
import { z } from "zod";

// ============================================================================
// Validation Issue Types
// ============================================================================

export const PaymentImportIssueTypeEnum = z.enum([
  "unmatched_supplier",
  "no_agreement_found",
  "no_valid_rule",
  "invalid_date",
  "invalid_amount",
]);

export type PaymentImportIssueType = z.infer<typeof PaymentImportIssueTypeEnum>;

export const PaymentImportIssueSeverityEnum = z.enum(["error", "warning"]);

export type PaymentImportIssueSeverity = z.infer<
  typeof PaymentImportIssueSeverityEnum
>;

// ============================================================================
// Parsed Data Schemas
// ============================================================================

export const ParsedPaymentSchema = z.object({
  paymentLineId: z.string(),
  supplier: z.string(),
  value: z.number(),
  grossAmount: z.number().nullable(),
  taxAmount: z.number().nullable(),
  receivedOn: z.string(), // Date string
  paymentMethod: z.string(),
  recordId: z.string().nullable(),
  externalReference: z.string().nullable(),
  currency: z.string().nullable(),
});

export type ParsedPayment = z.infer<typeof ParsedPaymentSchema>;

// ============================================================================
// Supplier Match Suggestion Schema
// ============================================================================

export const SupplierMatchSuggestionSchema = z.object({
  // The original supplier name from the CSV
  csvSupplierName: z.string(),
  // Best match from our database (null if no good match)
  suggestedSupplierId: z.string().uuid().nullable(),
  suggestedSupplierName: z.string().nullable(),
  // Similarity score (0-100)
  similarityScore: z.number(),
  // Payment stats for this supplier
  paymentCount: z.number(),
  totalPaymentAmount: z.number(),
});

export type SupplierMatchSuggestion = z.infer<
  typeof SupplierMatchSuggestionSchema
>;

// ============================================================================
// Supplier Mapping Schema (user-confirmed)
// ============================================================================

export const SupplierMappingSchema = z.object({
  csvSupplierName: z.string(),
  supplierId: z.string().uuid(),
});

export type SupplierMapping = z.infer<typeof SupplierMappingSchema>;

// ============================================================================
// Validation Issue Schema
// ============================================================================

export const ValidationIssueSchema = z.object({
  type: PaymentImportIssueTypeEnum,
  severity: PaymentImportIssueSeverityEnum,
  message: z.string(),
  // Additional context
  supplierName: z.string().nullable(),
  paymentLineId: z.string().nullable(),
  amount: z.number().nullable(),
});

export type ValidationIssue = z.infer<typeof ValidationIssueSchema>;

// ============================================================================
// Step 1: Parse Payment Files (returns supplier match suggestions)
// ============================================================================

export const ParsePaymentFilesRequestSchema = z.object({
  paymentsCsvBase64: z.string(),
});

export const ParsePaymentFilesResponseSchema = z.object({
  // Parsed payments
  payments: z.array(ParsedPaymentSchema),
  // Supplier match suggestions (one per unique supplier in CSV)
  supplierSuggestions: z.array(SupplierMatchSuggestionSchema),
  // Stats
  totalPayments: z.number(),
  uniqueSuppliers: z.number(),
  totalAmount: z.number(),
});

export type ParsePaymentFilesRequest = z.infer<
  typeof ParsePaymentFilesRequestSchema
>;
export type ParsePaymentFilesResponse = z.infer<
  typeof ParsePaymentFilesResponseSchema
>;

// ============================================================================
// Step 2: Import Payments (with user-confirmed supplier mappings)
// ============================================================================

export const ImportPaymentsRequestSchema = z.object({
  paymentsCsvBase64: z.string(),
  // User-confirmed supplier mappings
  supplierMappings: z.array(SupplierMappingSchema),
});

export const SupplierImportResultSchema = z.object({
  supplierName: z.string(),
  supplierId: z.string().uuid(),
  agreementId: z.string().uuid(),
  agreementName: z.string(),
  paymentsCreated: z.number(),
  totalPaymentAmount: z.number(),
});

export type SupplierImportResult = z.infer<typeof SupplierImportResultSchema>;

export const ImportPaymentsResponseSchema = z.object({
  success: z.boolean(),
  // Totals
  paymentsCreated: z.number(),
  totalPaymentAmount: z.number(),
  // Skipped
  skippedPayments: z.number(),
  // Breakdown by supplier
  bySupplier: z.array(SupplierImportResultSchema),
  // Warnings that didn't block import
  warnings: z.array(z.string()),
  // Validation issues (for suppliers without agreements, etc.)
  validationIssues: z.array(ValidationIssueSchema),
});

export type ImportPaymentsRequest = z.infer<typeof ImportPaymentsRequestSchema>;
export type ImportPaymentsResponse = z.infer<
  typeof ImportPaymentsResponseSchema
>;

// ============================================================================
// Get All Suppliers (for dropdown)
// ============================================================================

export const GetSuppliersForMappingResponseSchema = z.object({
  suppliers: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
    }),
  ),
});

export type GetSuppliersForMappingResponse = z.infer<
  typeof GetSuppliersForMappingResponseSchema
>;

// ============================================================================
// Contract Definitions
// ============================================================================

export const historicalPaymentImportContracts = {
  parsePaymentFiles: oc
    .input(ParsePaymentFilesRequestSchema)
    .output(ParsePaymentFilesResponseSchema)
    .route({
      method: "POST",
      summary: "Preview a historical rebate payments file",
      description:
        "Parses an uploaded rebate payments CSV, removing duplicates, and returns the payments with fuzzy-matched supplier suggestions, counts, and totals. Saves nothing.",
    }),

  getSuppliersForMapping: oc
    .output(GetSuppliersForMappingResponseSchema)
    .route({
      method: "GET",
      summary: "List suppliers for payment import mapping",
      description:
        "Lists the organization's suppliers (ID and name) for mapping supplier names in an imported rebate payments file.",
    }),

  importPayments: oc
    .input(ImportPaymentsRequestSchema)
    .output(ImportPaymentsResponseSchema)
    .route({
      method: "POST",
      summary: "Import historical rebate payments",
      description:
        "Imports rebate payments from an uploaded CSV using confirmed supplier mappings, attaching each to the supplier's agreement. Returns created and skipped counts, per-supplier totals, and validation issues.",
    }),
} as const;
